import { getFetch } from '@legacy/utils/fetcher';
import { concatQueries, getPathWithQueries } from '@legacy/utils/url/url';
import { GET_UNALLOCATED_TASKS_KEY } from './getUnallocatedTasksAPI.constants';
import { TUnallocatedTasksRes } from './getUnallocatedTasksAPI.types';
import { ASSIGN_TAKE_ITEMS } from '../../constants';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import logger from '@/legacy/utils/logger';

const GET_UNALLOCATED_TASKS_URL_PATH =
  '/api/v2/projects/{projectId}/tasks-unallocated';

interface IGetUnallocatedTasks {
  projectId: string;
  userId: string;
  isIncludeParentTask?: boolean;
  searchText?: string;
  take?: number;
  skip?: string;
}

const getUnallocatedTasks = ({
  isIncludeParentTask,
  projectId,
  searchText,
  userId,
  take = ASSIGN_TAKE_ITEMS,
  skip,
}: IGetUnallocatedTasks): Promise<TUnallocatedTasksRes> =>
  getFetch({
    path: `${getPathWithQueries(
      GET_UNALLOCATED_TASKS_URL_PATH.replace('{projectId}', projectId),
      {
        isIncludeParentTask,
        searchText: searchText?.trim(),
        userId,
      },
    )}${concatQueries(
      [
        { param: 'skip', value: skip },
        { param: 'take', value: String(take) },
      ],
      true,
    )}`,
    key: GET_UNALLOCATED_TASKS_KEY,
  });

export const useGetUnallocatedTasks = (
  params: IGetUnallocatedTasks,
  enableQuery?: boolean,
) => {
  const { isIncludeParentTask, projectId, searchText, userId } = params;
  const enabled = enableQuery && !!projectId && !!userId;

  const {
    data: tempData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    ...queryResult
  } = useInfiniteQuery({
    queryKey: [
      GET_UNALLOCATED_TASKS_KEY,
      isIncludeParentTask,
      projectId,
      searchText,
      userId,
    ],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const pageParamNumber = Number(pageParam);
      let response: TUnallocatedTasksRes;

      try {
        response = await getUnallocatedTasks({
          ...params,
          take: ASSIGN_TAKE_ITEMS,
          skip: String(pageParamNumber * ASSIGN_TAKE_ITEMS),
        });
      } catch (err) {
        console.error('Failed to fetch unallocated tasks', err);
        throw err;
      }

      return {
        items: response.entities ?? [], // Assuming response is an array of items
        nextSkip:
          (response.entities?.length ?? 0) < ASSIGN_TAKE_ITEMS
            ? undefined
            : pageParamNumber + 1,
      };
    },
    getNextPageParam: (lastPage) =>
      lastPage.items?.length === 0 ||
      (lastPage.items?.length ?? 0) < ASSIGN_TAKE_ITEMS
        ? null
        : lastPage.nextSkip,
    enabled,
  });

  const observerRef = useRef<HTMLLIElement | null>(null);

  // Store observer instance in a ref to maintain it between renders
  const observerInstanceRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Only create new observer if one doesn't exist already
    if (!observerInstanceRef.current && observerRef.current) {
      observerInstanceRef.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            entries.length > 0 &&
            hasNextPage &&
            !isFetchingNextPage &&
            enabled !== false
          ) {
            fetchNextPage();
          }
        },
        { threshold: 0.5 },
      );

      if (observerRef.current) {
        observerInstanceRef.current.observe(observerRef.current);
      }
    }

    // If observer exists but target element changed, reconnect it
    if (observerInstanceRef.current && observerRef.current) {
      // Ensure we're observing the current ref
      observerInstanceRef.current.disconnect();
      observerInstanceRef.current.observe(observerRef.current);
    }

    return () => {
      // Don't disconnect the observer during normal renders
      // Only clean up if component is unmounting (not on every filter change)
      if (enabled === false) {
        logger.info(
          'IntersectionObserver temporarily disabled, not disconnecting',
        );
      }
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, enabled]);

  // Add cleanup effect that only runs on unmount
  useEffect(
    () => () => {
      // This will only run when component unmounts
      if (observerInstanceRef.current) {
        observerInstanceRef.current.disconnect();
        observerInstanceRef.current = null;
      }
    },
    [],
  );

  const data = useMemo(
    () => tempData?.pages?.flatMap((page) => page.items) ?? [],
    [tempData?.pages],
  );

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    observerRef,
    latestPageChildrenLength: tempData?.pages?.at(-1)?.items?.length ?? 0,
    ...queryResult,
  };
};
